import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faUser, faLink, faCertificate } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { closingDateBadge } from './closingDateBadge';
import { clientShort } from './clientShort';
import ProjectQuickModal from './components/ProjectQuickModal';
import SlotQuickModal from './components/SlotQuickModal';
import VagueBadge from './components/VagueBadge';
import ConfirmModal from './components/ConfirmModal';
import PaymentRelancePrompt from './components/PaymentRelancePrompt';
import { makeKanbanCollisionDetection } from './kanbanCollision';
import './ProjectKanban.css';

const DEPT_STYLE = {
  define:    { bg: '#e8f0fe', color: '#1967d2', label: 'Define' },
  voa:       { bg: '#fef7cd', color: '#a16207', label: 'Voa' },
  immersion: { bg: '#fce8e6', color: '#c5221f', label: 'Immersion' },
};

const CLOSING_STEPS = [
  { key: 'livrables', label: 'Livrables', color: '#d97706' },
  { key: 'reception', label: 'Réception', color: '#0891b2' },
  { key: 'facturation', label: 'Facturation', color: '#7c3aed' },
  { key: 'attente_paiement', label: 'En attente de paiement', color: '#ea580c' },
  { key: 'encaisse', label: 'Encaissé', color: '#059669' },
];

const ClosingCard = ({ item, onClick, isDragOverlay }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${item.type}-${item.id}`,
    data: { item },
    disabled: isDragOverlay,
  });

  const style = isDragOverlay ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const isCati = item.survey && item.survey.poste_qcode !== 'ref';
  const dateBadge = closingDateBadge(item);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`project-card${isDragOverlay ? ' drag-overlay-card' : ''}`}
      onClick={() => onClick && onClick(item)}
    >
      {/* Badges: department + type */}
      <div className="project-card-badges">
        {item.department && (() => {
          const dept = DEPT_STYLE[item.department] || DEPT_STYLE.immersion;
          return <span className="badge-dept" style={{ background: dept.bg, color: dept.color }}>{dept.label}</span>;
        })()}
        {item.survey && (
          <span style={{
            fontSize: '0.6rem', fontWeight: 600, padding: '2px 7px', borderRadius: 3,
            background: isCati ? '#f3e8ff' : '#dbeafe', color: isCati ? '#7c3aed' : '#0284c7',
          }}>{isCati ? 'CATI' : 'F2F'}</span>
        )}
        <VagueBadge wave={item.survey_wave} style={{ fontSize: '0.6rem', padding: '2px 7px' }} />
        {dateBadge && (
          <span className={dateBadge.blink ? 'closing-badge-blink' : ''}
            style={{ fontSize: '0.6rem', fontWeight: 600, padding: '2px 7px', borderRadius: 3, background: dateBadge.bg, color: dateBadge.color, marginLeft: 'auto' }}>
            {dateBadge.text}
          </span>
        )}
      </div>
      {/* Name */}
      <div className="project-card-name">{item.client_name && <span style={{ color: '#9e9e9e', fontWeight: 600 }}>{clientShort(item.client_name, item.client_abbreviation)} - </span>}{item.name}</div>
      {/* Survey title */}
      {item.survey && (
        <div style={{ fontSize: '0.68rem', color: '#9e9e9e', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.survey.title} <span style={{ color: '#bdbdbd' }}>({item.survey.limesurvey_id})</span>
          {(item.linked_surveys?.length || 0) > 1 && (
            <span style={{ color: '#7c3aed', fontWeight: 600 }}> +{item.linked_surveys.length - 1}</span>
          )}
        </div>
      )}
      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.64rem', color: '#9e9e9e' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {item.client_name && <><FontAwesomeIcon icon={faBuilding} size="xs" style={{ opacity: 0.35 }} />{item.client_name}</>}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {item.assignees?.length > 0 && <><FontAwesomeIcon icon={faUser} size="xs" style={{ opacity: 0.35 }} />{item.assignees.map(a => a.name).join(', ')}</>}
        </span>
      </div>
    </div>
  );
};

const ClosingColumn = ({ step, items, onCardClick, onProjectClick }) => {
  const { setNodeRef, isOver } = useDroppable({ id: step.key });
  const sortableIds = items.map(i => `${i.type}-${i.id}`);

  // Group slots by project, keep project cards separate
  const projectCards = items.filter(i => i.type === 'project');
  const slotsByProject = {};
  items.filter(i => i.type === 'slot').forEach(item => {
    if (!slotsByProject[item.project_id]) slotsByProject[item.project_id] = { name: item.project_name, items: [] };
    slotsByProject[item.project_id].items.push(item);
  });

  return (
      <div ref={setNodeRef} className={`kanban-col-body${isOver ? ' is-over' : ''}`}>
        {items.length === 0 ? (
          <div className="kanban-col-empty">
            <span style={{ fontSize: '1.2rem', opacity: 0.3 }}>○</span>
            Aucun élément
          </div>
        ) : (
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {/* Project cards (projects without slots) */}
            {projectCards.map(item => (
              <ClosingCard key={`${item.type}-${item.id}`} item={item} onClick={onCardClick} />
            ))}

            {/* Slot groups with project header */}
            {Object.entries(slotsByProject).map(([projectId, group]) => (
              <div key={projectId} style={{ marginBottom: 12 }}>
                <div
                  onClick={() => onProjectClick && onProjectClick(parseInt(projectId), group.name)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px', marginBottom: 4,
                    cursor: 'pointer', borderRadius: 6,
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#eee'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 3, height: 16, borderRadius: 2, background: step.color, opacity: 0.6, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#424242', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {group.name}
                  </span>
                  <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#9e9e9e', background: '#e8e8e8', padding: '1px 7px', borderRadius: 10 }}>
                    {group.items.length}
                  </span>
                </div>
                {group.items.map(item => (
                  <ClosingCard key={`${item.type}-${item.id}`} item={item} onClick={onCardClick} />
                ))}
              </div>
            ))}
          </SortableContext>
        )}
      </div>
  );
};

const AttestationCard = ({ item }) => {
  const isCati = item.survey && item.survey.poste_qcode !== 'ref';
  const dept = DEPT_STYLE[item.department] || DEPT_STYLE.immersion;
  return (
    <div className="project-card" style={{ borderLeft: '3px solid #dc2626', minHeight: 124, display: 'flex', flexDirection: 'column' }}>
      <div className="project-card-badges">
        <span className="badge-dept" style={{ background: dept.bg, color: dept.color }}>{dept.label}</span>
        {item.survey && (
          <span style={{ fontSize: '0.6rem', fontWeight: 600, padding: '2px 7px', borderRadius: 3, background: isCati ? '#f3e8ff' : '#dbeafe', color: isCati ? '#7c3aed' : '#0284c7' }}>{isCati ? 'CATI' : 'F2F'}</span>
        )}
        <VagueBadge wave={item.survey_wave} style={{ fontSize: '0.58rem', padding: '2px 7px' }} />
        <span style={{ fontSize: '0.58rem', fontWeight: 600, padding: '2px 7px', borderRadius: 3, background: '#fef2f2', color: '#dc2626', marginLeft: 'auto' }}>
          <FontAwesomeIcon icon={faCertificate} size="xs" style={{ marginRight: 3 }} />En attente
        </span>
      </div>
      <div className="project-card-name">
        {item.client_name && <span style={{ color: '#9e9e9e', fontWeight: 600 }}>{clientShort(item.client_name, item.client_abbreviation)} - </span>}{item.name}
      </div>
      {/* Sub-line: project name for slots, reserved height otherwise */}
      <div style={{ fontSize: '0.66rem', color: '#9e9e9e', minHeight: '1em', marginBottom: 4 }}>
        {item.type === 'slot' ? item.project_name : ''}
      </div>
      {/* Client — pushed to bottom */}
      <div style={{ fontSize: '0.66rem', color: '#9e9e9e', display: 'flex', alignItems: 'center', gap: 4, marginTop: 'auto' }}>
        {item.client_name && <><FontAwesomeIcon icon={faBuilding} size="xs" style={{ opacity: 0.35 }} />{item.client_name}</>}
      </div>
    </div>
  );
};

const ClosingKanban = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const [quickModal, setQuickModal] = useState({ open: false, type: null, project: null, slot: null });
  const [attestationData, setAttestationData] = useState([]);

  // Vue et filtres vivent dans l'URL, comme /quotas et /frais-deplacement : ouvrir une fiche
  // puis revenir en arrière restaure la recherche au lieu de la perdre. Le filtrage étant fait
  // côté client ici, on peut écrire dans l'URL à chaque frappe sans appel réseau.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'attestation' ? 'attestation' : 'closing'; // 'closing' | 'attestation'
  const search = searchParams.get('search') || '';
  const deptFilter = searchParams.get('department') || '';
  const typeFilter = searchParams.get('type') || '';

  // replace : les filtres ne doivent pas empiler une entrée d'historique par frappe, sinon
  // le bouton Retour reparcourt la saisie lettre par lettre.
  const updateParams = useCallback((next) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      Object.entries(next).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) p.delete(key);
        else p.set(key, value);
      });
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const setView = useCallback((value) => updateParams({ view: value === 'closing' ? '' : value }), [updateParams]);
  const setSearch = useCallback((value) => updateParams({ search: value }), [updateParams]);
  const setDeptFilter = useCallback((value) => updateParams({ department: value }), [updateParams]);
  const setTypeFilter = useCallback((value) => updateParams({ type: value }), [updateParams]);

  const matchesFilter = useCallback((item) => {
    if (deptFilter && item.department !== deptFilter) return false;
    if (typeFilter && item.project_type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [item.name, item.project_name, item.client_name, item.survey?.title]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }, [search, deptFilter, typeFilter]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const collisionDetection = useMemo(
    () => makeKanbanCollisionDetection(CLOSING_STEPS.map(s => s.key)), []);
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });
  const [relancePrompt, setRelancePrompt] = useState({ open: false, item: null });

  const fetchData = useCallback(async () => {
    try {
      const [closingRes, attRes] = await Promise.all([
        api.get('/projects/closing-kanban'),
        api.get('/projects/attestation-tracking'),
      ]);
      setData(closingRes.data.data || {});
      setAttestationData(attRes.data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openModalForItem = (item) => {
    if (item.type === 'slot') {
      setQuickModal({ open: true, type: 'slot', project: { id: item.project_id, name: item.project_name, client: { name: item.client_name } }, slot: { ...item, id: item.id, assignees: item.assignees, step: 'closing', closing_step: item.closing_step || 'reception' } });
    } else {
      setQuickModal({ open: true, type: 'project', project: { id: item.id, name: item.name, client: { name: item.client_name }, assignees: item.assignees, notes: item.notes, attestation_requested: item.attestation_requested, attestation_email: item.attestation_email, attestation_request_sent_at: item.attestation_request_sent_at, attestation_relance_count: item.attestation_relance_count, department: item.department, project_type: item.project_type, closing_step: item.closing_step, livrables_deadline: item.livrables_deadline, reception_date: item.reception_date, facturation_date: item.facturation_date, attente_paiement_date: item.attente_paiement_date, payment_date: item.payment_date, payment_relance: item.payment_relance, can_set_payment_relance: item.can_set_payment_relance, step: 'closing' }, slot: null });
    }
  };

  const onDragStart = ({ active }) => {
    const d = active.data.current;
    if (d?.item) setActiveItem(d.item);
  };

  const onDragEnd = ({ active, over }) => {
    setActiveItem(null);
    if (!over) return;

    const activeId = String(active.id);
    let toStep = String(over.id);

    // If dropped on a card, find its column
    if (toStep.includes('-')) {
      for (const cs of CLOSING_STEPS) {
        if ((data[cs.key] || []).some(i => `${i.type}-${i.id}` === toStep)) {
          toStep = cs.key;
          break;
        }
      }
    }

    if (!CLOSING_STEPS.some(s => s.key === toStep)) return;

    const [type, idStr] = activeId.split('-');
    const id = parseInt(idStr);

    // Find current step
    let fromStep = null;
    for (const cs of CLOSING_STEPS) {
      if ((data[cs.key] || []).some(i => i.type === type && i.id === id)) {
        fromStep = cs.key;
        break;
      }
    }

    if (!fromStep || fromStep === toStep) return;

    const moved = (data[fromStep] || []).find(i => i.type === type && i.id === id);

    // Optimistic move
    setData(prev => {
      const newData = { ...prev };
      const item = (newData[fromStep] || []).find(i => i.type === type && i.id === id);
      if (!item) return prev;
      newData[fromStep] = (newData[fromStep] || []).filter(i => !(i.type === type && i.id === id));
      newData[toStep] = [...(newData[toStep] || []), { ...item, closing_step: toStep }];
      return newData;
    });

    // API call
    api.post('/projects/closing-transition', { type, id, to_step: toStep })
      .then(() => {
        fetchData();
        // Entrée en attente de paiement : demander le délai accordé au client.
        if (toStep === 'attente_paiement' && moved && moved.can_set_payment_relance) {
          setRelancePrompt({ open: true, item: moved });
        }
      })
      .catch(err => {
        // fetchData() rolls the optimistic move back; without a message the card just
        // snaps to its old column and looks like drag-and-drop failed.
        setErrorModal({
          open: true,
          message: err.response?.data?.message || "Le déplacement n'a pas pu être enregistré.",
        });
        fetchData();
      });
  };

  const totalCount = CLOSING_STEPS.reduce((s, cs) => s + (data[cs.key] || []).length, 0);

  return (
    <div className="kanban-page">
      <PaymentRelancePrompt
        isOpen={relancePrompt.open}
        onClose={() => setRelancePrompt({ open: false, item: null })}
        projectId={relancePrompt.item?.project_id}
        slotId={relancePrompt.item?.type === 'slot' ? relancePrompt.item.id : null}
        itemName={relancePrompt.item?.name}
        currentMode={relancePrompt.item?.payment_relance?.mode}
        onSaved={() => fetchData()}
      />

      {/* Top bar with view tabs */}
      <div className="kanban-topbar">
        <div className="kanban-topbar-left" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button onClick={() => setView('closing')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
              fontSize: '1.05rem', fontWeight: 700,
              color: view === 'closing' ? '#242424' : '#bdbdbd',
              borderBottom: view === 'closing' ? '2px solid #dc2626' : '2px solid transparent',
            }}>
            {/* Même raison que sur le kanban projets : pas de compteur tant que les données
                ne sont pas là, sinon il affiche 0. */}
            Closing {view === 'closing' && !loading && <span className="kanban-topbar-count">{totalCount}</span>}
          </button>
          <button onClick={() => setView('attestation')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
              fontSize: '1.05rem', fontWeight: 700,
              color: view === 'attestation' ? '#242424' : '#bdbdbd',
              borderBottom: view === 'attestation' ? '2px solid #dc2626' : '2px solid transparent',
            }}>
            Attest. Réf. {view === 'attestation' && !loading && <span className="kanban-topbar-count">{attestationData.filter(matchesFilter).length}</span>}
          </button>
        </div>
        <div className="kanban-topbar-filters">
          <input className="kanban-filter-input" type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 180 }} />
          <select className="kanban-filter-select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
            <option value="">Département</option>
            <option value="define">Define</option>
            <option value="voa">Voa</option>
            <option value="immersion">Immersion</option>
          </select>
          <select className="kanban-filter-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">Type</option>
            <option value="contrat">Contrat</option>
            <option value="appel_offre">Appel d'Offre</option>
            <option value="consultation_restreinte">Consultation Restreinte</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center" style={{ flex: 1 }}>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
        </div>
      ) : view === 'closing' ? (
        <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="kanban-board-wrapper">
            <div className="kanban-board">
              {CLOSING_STEPS.map(step => (
                <div key={step.key} className="kanban-col" style={{ '--col-accent': step.color }}>
                  <div className="kanban-step-tab-inline" style={{ borderBottomColor: step.color }}>
                    <span className="kanban-step-tab-dot" style={{ background: step.color }} />
                    {step.label}
                    <span className="kanban-step-tab-count">{(data[step.key] || []).filter(matchesFilter).length}</span>
                  </div>
                <ClosingColumn
                  key={step.key}
                  step={step}
                  items={(data[step.key] || []).filter(matchesFilter)}
                  onCardClick={openModalForItem}
                  onProjectClick={(projectId) => {
                    const p = data[step.key]?.find(i => i.type === 'project' && i.id === projectId);
                    if (p) openModalForItem(p);
                    else navigate(`/projects/${projectId}`);
                  }}
                />
                </div>
              ))}
            </div>
          </div>

          <DragOverlay dropAnimation={null}>
            {activeItem && (
              <div style={{ width: '260px' }}>
                <ClosingCard item={activeItem} isDragOverlay />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        /* Attestation tracking view */
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 2px' }}>
          {attestationData.filter(matchesFilter).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9e9e9e' }}>
              <FontAwesomeIcon icon={faCertificate} style={{ fontSize: '2rem', color: '#e0e0e0', marginBottom: 12 }} />
              <div style={{ fontSize: '0.85rem' }}>Aucune attestation de référence en attente</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, padding: '8px 0', alignItems: 'start' }}>
              {attestationData.filter(matchesFilter).map(item => (
                <div key={`${item.type}-${item.id}`} onClick={() => openModalForItem({ ...item, attestation_requested: true })} style={{ cursor: 'pointer' }}>
                  <AttestationCard item={item} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ProjectQuickModal
        isOpen={quickModal.open && quickModal.type === 'project'}
        onClose={() => { setQuickModal({ open: false, type: null, project: null, slot: null }); fetchData(); }}
        project={quickModal.project}
        onUpdate={() => fetchData()}
      />
      <SlotQuickModal
        isOpen={quickModal.open && quickModal.type === 'slot'}
        onClose={() => { setQuickModal({ open: false, type: null, project: null, slot: null }); fetchData(); }}
        slot={quickModal.slot}
        project={quickModal.project}
        onUpdate={() => fetchData()}
      />
      {/* Alert-only (no onConfirm): explains a transition the server refused. */}
      <ConfirmModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: '' })}
        title="Déplacement impossible"
        message={errorModal.message}
        variant="danger"
        dismissOnBackdrop={false}
      />
    </div>
  );
};

export default ClosingKanban;
